import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGameLogic } from '../../hooks/useGameLogic';
import TennisBoardScore from './TennisBoardScore';
import QuestionCard from './QuestionCard';
import AnswerOptions from './AnswerOptions';
import TennisMiniGame from './TennisMiniGame';
import TennisFirstPerson from './TennisFirstPerson';
import type { ChallengeType, Difficulty } from '../../utils/mathProblems';

const TYPE_LABELS: Record<ChallengeType, string> = {
  multiplication: 'Multiplication',
  volume: 'Volume',
  division: 'Division',
};

const TYPE_ICONS: Record<ChallengeType, string> = {
  multiplication: 'ri-close-line',
  volume: 'ri-box-3-line',
  division: 'ri-divide-line',
};

interface Props {
  challengeType: ChallengeType;
  difficulty: Difficulty;
}

export default function GameBoard({ challengeType, difficulty }: Props) {
  const { state, startGame, submitAnswer, nextQuestion, onMiniGameEnd } = useGameLogic(challengeType, difficulty);
  const navigate = useNavigate();
  const [gameMode, setGameMode] = useState<'vs-cpu' | 'vs-player'>('vs-cpu');
  const [perspective, setPerspective] = useState<'third' | 'first'>('third');

  const opponentLabel = gameMode === 'vs-player' ? 'Player 2' : 'CPU';
  const opponentShort = gameMode === 'vs-player' ? 'P2' : 'CPU';

  const handleAnswer = useCallback((answer: string) => {
    if (state.answerState !== 'idle') return;
    submitAnswer(answer);
  }, [state.answerState, submitAnswer]);

  // Not started
  if (!state.gameStarted) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-6">
        <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full">
          <i className={`${TYPE_ICONS[challengeType]} text-4xl text-green-700`} />
        </div>
        <div className="text-center">
          <h2 className="text-2xl font-bold text-green-900" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em' }}>
            {TYPE_LABELS[challengeType]} Challenge
          </h2>
          <p className="text-gray-500 mt-1 capitalize">Difficulty: <strong>{difficulty}</strong></p>
          <p className="text-gray-400 text-sm mt-1">Answer correctly → play a tennis rally → win the point!</p>
        </div>

        {/* Mode selector */}
        <div className="flex flex-col items-center gap-2">
          <p className="text-xs text-gray-400 uppercase tracking-widest font-medium">Game Mode</p>
          <div className="flex gap-2 bg-gray-100 rounded-full p-1">
            <button
              onClick={() => setGameMode('vs-cpu')}
              className={`px-5 py-1.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                gameMode === 'vs-cpu'
                  ? 'bg-green-700 text-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className="ri-robot-line mr-1.5" />
              vs CPU
            </button>
            <button
              onClick={() => setGameMode('vs-player')}
              className={`px-5 py-1.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                gameMode === 'vs-player'
                  ? 'bg-orange-500 text-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className="ri-group-line mr-1.5" />
              2 Players
            </button>
          </div>
          {gameMode === 'vs-player' && (
            <div className="mt-1 text-center bg-orange-50 border border-orange-200 rounded-xl px-5 py-2.5 text-xs text-orange-700 max-w-xs">
              <p className="font-semibold mb-1">
                <i className="ri-keyboard-line mr-1" />
                Two-Player Controls
              </p>
              <p><strong>P1</strong>: A/D or ←/→ to move</p>
              <p><strong>P2</strong>: Q/E to move</p>
            </div>
          )}
        </div>

        {/* Perspective selector */}
        <div className="flex flex-col items-center gap-2">
          <p className="text-xs text-gray-400 uppercase tracking-widest font-medium">Camera View</p>
          <div className="flex gap-2 bg-gray-100 rounded-full p-1">
            <button
              onClick={() => setPerspective('third')}
              className={`px-5 py-1.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                perspective === 'third'
                  ? 'bg-green-700 text-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className="ri-vidicon-line mr-1.5" />
              3rd Person
            </button>
            <button
              onClick={() => setPerspective('first')}
              className={`px-5 py-1.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                perspective === 'first'
                  ? 'bg-sky-600 text-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className="ri-eye-line mr-1.5" />
              1st Person
            </button>
          </div>
          <p className="text-xs text-gray-400">
            {perspective === 'third'
              ? 'Top-down court view — see both players on screen'
              : 'Immersive view from behind your player'}
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => navigate('/level-selector')}
            className="px-5 py-2 rounded-full border border-gray-300 text-gray-600 font-medium text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap"
          >
            Change Level
          </button>
          <button
            onClick={startGame}
            className={`px-7 py-2 rounded-full text-white font-semibold text-sm cursor-pointer whitespace-nowrap transition-colors ${
              gameMode === 'vs-player' ? 'bg-orange-500 hover:bg-orange-400' : 'bg-green-700 hover:bg-green-600'
            }`}
          >
            Start Match
          </button>
        </div>
      </div>
    );
  }

  // Match over
  if (state.matchState.matchOver) {
    const won = state.matchState.winner === 'player';
    const winnerName = won ? 'Player 1' : opponentLabel;
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-6 text-center">
        <div className={`w-24 h-24 flex items-center justify-center rounded-full ${won ? 'bg-lime-100' : 'bg-red-100'}`}>
          <i className={`text-5xl ${won ? 'ri-trophy-fill text-yellow-500' : 'ri-emotion-sad-line text-red-400'}`} />
        </div>
        <div>
          <h2 className="text-3xl font-bold" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em', color: won ? '#166534' : '#b91c1c' }}>
            {gameMode === 'vs-player'
              ? (won ? 'P1 WINS!' : 'P2 WINS!')
              : (won ? 'MATCH WON!' : 'MATCH LOST')}
          </h2>
          <p className="text-gray-500 mt-1">
            {gameMode === 'vs-player'
              ? `${winnerName} wins the match!`
              : (won ? 'Outstanding! You aced the math challenge!' : 'Keep practicing — the net is waiting!')}
          </p>
        </div>
        <div className="bg-green-900 rounded-xl px-8 py-4 text-white">
          <p className="text-green-300 text-xs uppercase tracking-widest mb-2">Final Score</p>
          <div className="flex gap-6 text-2xl font-bold">
            <span className="text-lime-400">
              {gameMode === 'vs-player' ? 'P1' : 'You'} {state.matchState.sets.filter(s => s.player > s.opponent).length}
            </span>
            <span className="text-gray-400">–</span>
            <span className="text-orange-400">
              {state.matchState.sets.filter(s => s.opponent > s.player).length} {opponentShort}
            </span>
          </div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => navigate('/level-selector')}
            className="px-5 py-2 rounded-full border border-gray-300 text-gray-600 font-medium text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap"
          >
            Change Level
          </button>
          <button
            onClick={startGame}
            className="px-7 py-2 rounded-full bg-green-700 text-white font-semibold text-sm hover:bg-green-600 cursor-pointer whitespace-nowrap"
          >
            Play Again
          </button>
        </div>
      </div>
    );
  }

  // Mini-game phase
  if (state.phase === 'mini-game') {
    return (
      <div className="flex flex-col gap-5">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
          <div className="flex flex-col items-center">
            {perspective === 'first' ? (
              <TennisFirstPerson
                key={state.roundNumber}
                onRallyEnd={onMiniGameEnd}
                difficulty={difficulty}
                opponentLabel={gameMode === 'vs-player' ? 'P2' : 'CPU'}
              />
            ) : (
              <TennisMiniGame
                key={state.roundNumber}
                onRallyEnd={onMiniGameEnd}
                difficulty={difficulty}
                mode={gameMode}
              />
            )}
          </div>
          <div className="flex flex-col gap-4">
            <TennisBoardScore
              matchState={state.matchState}
              playerLabel={gameMode === 'vs-player' ? 'Player 1' : 'You'}
              opponentLabel={opponentLabel}
            />
            <div className="bg-green-50 rounded-xl p-4 text-sm text-green-800">
              <p className="font-semibold mb-2">
                <i className="ri-gamepad-line mr-1" />
                Rally Controls
              </p>
              {perspective === 'first' ? (
                <ul className="space-y-1 text-xs text-green-700">
                  <li>• <kbd className="bg-white border border-green-200 rounded px-1">A</kbd> / <kbd className="bg-white border border-green-200 rounded px-1">D</kbd> or <kbd className="bg-white border border-green-200 rounded px-1">←</kbd> <kbd className="bg-white border border-green-200 rounded px-1">→</kbd> — Move</li>
                  <li>• First-person immersive view</li>
                  <li>• Don&apos;t let the ball pass you!</li>
                </ul>
              ) : gameMode === 'vs-player' ? (
                <ul className="space-y-1 text-xs text-green-700">
                  <li>• <strong>P1</strong>: <kbd className="bg-white border border-green-200 rounded px-1">A</kbd> / <kbd className="bg-white border border-green-200 rounded px-1">D</kbd> or <kbd className="bg-white border border-green-200 rounded px-1">←</kbd> <kbd className="bg-white border border-green-200 rounded px-1">→</kbd></li>
                  <li>• <strong>P2</strong>: <kbd className="bg-white border border-green-200 rounded px-1">Q</kbd> / <kbd className="bg-white border border-green-200 rounded px-1">E</kbd> to move</li>
                  <li>• Don&apos;t let the ball bounce twice!</li>
                </ul>
              ) : (
                <ul className="space-y-1 text-xs text-green-700">
                  <li>• <kbd className="bg-white border border-green-200 rounded px-1">A</kbd> / <kbd className="bg-white border border-green-200 rounded px-1">D</kbd> or <kbd className="bg-white border border-green-200 rounded px-1">←</kbd> <kbd className="bg-white border border-green-200 rounded px-1">→</kbd> — Move</li>
                  <li>• Don&apos;t let the ball pass your side!</li>
                  <li>• CPU difficulty scales with your math level</li>
                </ul>
              )}
            </div>
            <div className="bg-amber-50 rounded-xl p-3 text-xs text-amber-700 border border-amber-200">
              <i className="ri-lightbulb-line mr-1" />
              You answered correctly — now win the rally to claim your tennis point!
            </div>
            <button
              onClick={() => navigate('/level-selector')}
              className="py-2 rounded-xl border border-gray-200 text-gray-500 text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line mr-1" />
              Exit Match
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Result phase + Question phase
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
      {/* Left: Question / Result area */}
      <div className="flex flex-col gap-4">
        {state.phase === 'result' && (
          <div className={`rounded-xl px-5 py-4 flex items-center gap-3 ${
            state.answerState === 'wrong'
              ? 'bg-red-50 border border-red-200'
              : 'bg-green-50 border border-green-200'
          }`}>
            <div className={`w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0 ${
              state.answerState === 'wrong' ? 'bg-red-100' : 'bg-green-100'
            }`}>
              <i className={`text-xl ${
                state.answerState === 'wrong' ? 'ri-close-circle-line text-red-500' : 'ri-trophy-line text-green-600'
              }`} />
            </div>
            <div>
              {state.answerState === 'wrong' ? (
                <>
                  <p className="font-semibold text-red-700 text-sm">Wrong answer!</p>
                  <p className="text-red-500 text-xs mt-0.5">
                    {opponentLabel} takes the point automatically. The correct answer was{' '}
                    <strong>{state.question?.correctAnswer}</strong>
                  </p>
                </>
              ) : (
                <>
                  <p className="font-semibold text-green-700 text-sm">Rally complete!</p>
                  <p className="text-green-600 text-xs mt-0.5">
                    Ready for the next math question?
                  </p>
                </>
              )}
            </div>
          </div>
        )}

        {state.question && (
          <>
            <QuestionCard
              question={state.question}
              roundNumber={state.roundNumber}
              answerState={state.answerState}
              timeLeft={state.timeLeft}
              difficulty={difficulty}
            />
            <AnswerOptions
              question={state.question}
              onAnswer={handleAnswer}
              answerState={state.answerState}
              selectedAnswer={state.selectedAnswer}
              disabled={state.phase !== 'question'}
            />
            {state.phase === 'result' && !state.matchState.matchOver && (
              <button
                onClick={nextQuestion}
                className="w-full py-3 rounded-xl bg-green-700 text-white font-semibold hover:bg-green-600 cursor-pointer transition-colors whitespace-nowrap text-sm"
              >
                Next Question →
              </button>
            )}
          </>
        )}
      </div>

      {/* Right: Scoreboard */}
      <div className="flex flex-col gap-4">
        <TennisBoardScore
          matchState={state.matchState}
          playerLabel={gameMode === 'vs-player' ? 'Player 1' : 'You'}
          opponentLabel={opponentLabel}
        />
        <div className="bg-green-50 rounded-xl p-4 text-sm text-green-800">
          <p className="font-semibold mb-2">
            <i className="ri-information-line mr-1" />
            How It Works
          </p>
          <ul className="space-y-1 text-xs text-green-700">
            <li>• Answer correctly → play a tennis rally</li>
            <li>• Win the rally → you earn the point</li>
            <li>• Lose the rally → {opponentShort} earns the point</li>
            <li>• Wrong answer → {opponentShort} gets the point</li>
            <li>• First to win 2 sets wins the match</li>
          </ul>
        </div>
        <button
          onClick={() => navigate('/level-selector')}
          className="py-2 rounded-xl border border-gray-200 text-gray-500 text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line mr-1" />
          Exit Match
        </button>
      </div>
    </div>
  );
}
